import { useEffect, useState, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "components/Button";
import { getConsentContent } from "services/consent.service";
import { FormField } from "@/components/FormField";
import {
  IMPRINT_URL,
  MANDATORY_CONSENT,
  OUR_CONDITIONS,
} from "constants/general";
import { Loader } from "components/Loader";
import {
  BTN_SIZE,
  BTN_VARIANTS,
  RESPONSE_MESSAGES,
} from "constants/components";
import styles from "./AppConsent.module.scss";
import { APP_CONSENT, CONSENT_DATA } from "constants/AllStaticText";
import { Link } from "react-router-dom";
import { routes } from "constants/routes";
import { APP_CONSENT_MESSAGES } from "constants/error-loading";
import { useModal } from "hooks/useModal";
import { TextModalContent } from "components/TextModalContent";
import { deleteUserAccount } from "services/profile.services";
import { responseMessage } from "helpers/errorHandling.helper";
import { ModalType } from "models/common";
import { Typography } from "components/Typography";
import { CheckboxFieldConfig, FieldType } from "@/models/formField";
import { ValidationRules } from "@/helpers/formField.helper";
import parse from "html-react-parser";
import useAuth from "@/hooks/useAuth";
export interface AppConsentProps {
  consentCheckLoading: boolean;
  submitConsent: (consentData: Record<string, any>) => void;
}

export const AppConsent: React.FC<AppConsentProps> = ({
  consentCheckLoading,
  submitConsent,
}) => {
  const [consentContent, setConsentContent] = useState<
    CheckboxFieldConfig[] | null
  >(null); // State to hold consent content
  const [loading, setLoading] = useState<boolean>(false); // State to handle loading spinner
  const [submitLoader, setSubmitLoader] = useState<boolean>(false); // state to handle submit button click loader
  const [consentError, setConsentError] = useState<boolean>(false); // State to handle consent error
  const { showModal } = useModal();
  const { isAuthenticated } = useAuth();
  const methods = useForm({ mode: "onSubmit" });
  const { handleSubmit, setValue } = methods;

  // Effect to load consent content when consentCheckLoading is false
  useEffect(() => {
    // Fetch consent content from API
    const getContent = async () => {
      setLoading(true); // Show loading spinner while fetching content
      try {
        const response = await getConsentContent();
        if (response && response.status === 200) {
          setLoading(false); // Stop loading when content is fetched
          setConsentError(false);
          setConsentContent(configFormatter(response?.data?.data)); // Format and set consent content
        } else {
          setLoading(false);
          setConsentError(true);
        }
      } catch (error) {
        console.log(error); // Log any errors that occur during fetching
        setLoading(false);
        setConsentError(true);
      }
    };
    if (!consentCheckLoading) {
      getContent();
    }
  }, [consentCheckLoading]);

  /**
   * @description  To navigate to logout page and clear confirmation modal timer if existing.
   *
   */
  const navigateToLogout = () => {
    window.location.href = routes.logout.path; // navigate to logout page
  };
  const handleDeleteAccount = async () => {
    try {
      const response = await deleteUserAccount();
      if (response?.status === 200) {
        // show the delete info modal
        showModal({
          children: (
            <TextModalContent
              textContent="Please note that the deletion of your user account takes a few minutes to complete. If you attempt to create a new account with the same email adress in the meantime you may see your old data partially or fully synched back to the new account."
              isIconVisible={true}
            />
          ),
          type: ModalType.NORMAL,
          onOk: () => navigateToLogout(),
          onCancel: () => navigateToLogout(),
          showFooter: true,
          okText: "OK",
        });
      } else {
        responseMessage(RESPONSE_MESSAGES.ERROR);
      }
    } catch (error) {
      console.error(error);
      responseMessage(RESPONSE_MESSAGES.ERROR);
    }
  };

  // Function to generate RHF-compatible CheckboxFieldConfig based on consent items
  const configFormatter = (items: any): CheckboxFieldConfig[] => {
    return items?.map((item: any) => {
      const res: CheckboxFieldConfig = {
        type: FieldType.CHECKBOX,
        name: item?.consentId,
        label: "",
        defaultValue: [],
        options: [
          {
            label: item?.description,
            value: item?.consentId,
          },
        ],
        // Validators are added only for mandatory consent
        validation: item?.mandatory
          ? ValidationRules.custom(
              (value: any[]) => value && value.length > 0,
              MANDATORY_CONSENT.message,
            )
          : undefined,
      };
      return res;
    });
  };

  // Build OUR_CONDITIONS checkbox as a CheckboxFieldConfig for react-hook-form
  const ourConditionsCheckboxConfig: CheckboxFieldConfig = useMemo(
    () => ({
      type: FieldType.CHECKBOX,
      name: OUR_CONDITIONS.CHECKBOX.id,
      label: "",
      defaultValue: [],
      options: [
        {
          label: OUR_CONDITIONS.CHECKBOX.options[0].label as any,
          value: true,
        },
      ],
      validation: ValidationRules.custom(
        (value: any[]) => value && value.length > 0,
        "You must accept the Conditions of Use",
      ),
    }),
    [],
  );

  // Accept all consent checkboxes via react-hook-form setValue
  const acceptAllConsents = () => {
    // Check the our conditions checkbox
    setValue(OUR_CONDITIONS.CHECKBOX.id, [true], { shouldValidate: true });

    // Check all existing consent checkboxes
    if (consentContent && consentContent.length > 0) {
      consentContent.forEach((item) => {
        setValue(item.name, [item.name], { shouldValidate: true });
      });
    }
  };

  // Submit the consent response if RHF validation passes
  const submitConsentResponse = async (formData: any) => {
    setSubmitLoader(true);
    try {
      // Build consent data in the format ConsentLayout expects
      const consentPayload: Record<string, any> = {};
      if (consentContent) {
        consentContent.forEach((item) => {
          consentPayload[item.name] = formData[item.name] || [];
        });
      }

      // Pass formatted consent data directly to parent via callback
      submitConsent(consentPayload);
    } catch (error) {
      setSubmitLoader(false);
      console.log(error); // Log any errors during submission
    }
  };

  return (
    <FormProvider {...methods}>
      <div className={`${styles.overlay}`}>
        <div className={`${styles.modal}`}>
          {consentCheckLoading ? (
            <Loader
              loaderText={APP_CONSENT_MESSAGES.CHECK_LOADING}
              wrapperClassName={styles.custom_loader}
            />
          ) : loading ? (
            <Loader loaderText={APP_CONSENT_MESSAGES.LOADING} />
          ) : consentError ? (
            <Typography variant="p" title={APP_CONSENT_MESSAGES.ERROR}>
              {APP_CONSENT_MESSAGES.ERROR}
            </Typography>
          ) : (
            <div className={styles.consent_container}>
              <Typography variant="h2" title={APP_CONSENT.HEADING}>
                {APP_CONSENT.HEADING}
              </Typography>

              <div className={styles.checkboxes}>
                {!isAuthenticated && (
                  <FormField config={ourConditionsCheckboxConfig} />
                )}

                {/* Render checkboxes for consent items */}
                {consentContent &&
                  consentContent.length !== 0 &&
                  consentContent.map((item, index) => {
                    return (
                      <FormField key={`${item.name}_${index}`} config={item} />
                    );
                  })}
              </div>
              <div className={styles.btn_wrapper}>
                {/* Accept all consent checkboxes button */}
                <Button
                  className={styles.secondary_btn}
                  size={BTN_SIZE.DEFAULT}
                  type={BTN_VARIANTS.NEUTRAL}
                  onClick={acceptAllConsents}
                >
                  Accept All
                </Button>
                {/* Submit button for consent */}
                <Button
                  className={styles.primary_btn}
                  size={BTN_SIZE.DEFAULT}
                  type={BTN_VARIANTS.PRIMARY}
                  onClick={handleSubmit(submitConsentResponse)}
                  loading={submitLoader}
                >
                  agree and continue
                </Button>
              </div>
              <Typography variant="p" title={"consent text"}>
                {parse(CONSENT_DATA)}
              </Typography>
              <div className={styles.btn_link_wrapper}>
                <Link className={styles.link} to={routes.termsPrivacy.path}>
                  Conditions of Use
                </Link>
                <Link
                  className={styles.link}
                  onClick={(e) => {
                    e.preventDefault();
                    window?.OneTrust?.ToggleInfoDisplay();
                  }}
                  to={"#"}
                  // to={"javascript:void(0)"}
                >
                  Cookie Settings
                </Link>
                <Link className={styles.link} to={IMPRINT_URL}>
                  Imprint
                </Link>
                {!!isAuthenticated && (
                  <Button
                    className={styles.link}
                    type={BTN_VARIANTS.NEUTRAL}
                    size={BTN_SIZE.NEUTRAL}
                    onClick={() =>
                      showModal({
                        children: (
                          <TextModalContent
                            textContent="Do you want to delete your account?"
                            isIconVisible={true}
                          />
                        ),
                        type: ModalType.NORMAL,
                        onOk: () => handleDeleteAccount(),
                        showFooter: true,
                        okText: "Confirm",
                        cancelText: "Dismiss",
                      })
                    }
                  >
                    Delete Account
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};
